import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { getEnv } from '@ctct/ctct-helpers';
import { Button } from '@ctct/ui-react';

import usePromoCodeControl from './hooks/usePromoCodeControl';
import { useInboxPreviewContext } from './InboxPreviewContext';
import PageStates from './PageStates';
import PromoCodeControl from './PromoCodeControl';
import { getInboxPreviewPricing } from '../../constants/inboxPreviewPricing';
import { getSupportPhoneNumber } from '../../utils/getSupportPhoneNumber';

const homePage = '/pages/dashboard/home';

const IBP_AMOUNT = 25;

const InboxPreviewPurchasePanel = ({ customer }) => {
  const { t } = useTranslation();

  const [state, dispatch] = useInboxPreviewContext();
  const { processingPayment, rating, embedded } = state;

  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [showTermsError, setShowTermsError] = useState(false);

  const { addPromo, removePromo, processPayment } = usePromoCodeControl();

  // Get customer info to determine pricing and support phone
  const countryCode = customer?.getCountryCode();
  const { price, currency } = getInboxPreviewPricing(countryCode);
  const supportPhone = getSupportPhoneNumber(countryCode);

  const clickAccept = () => {
    if (!agreedToTerms) {
      setShowTermsError(true);
    } else {
      setShowTermsError(false);

      if (rating) {
        dispatch({ type: 'setProcessingPayment', value: true });
        processPayment();
      }
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const embeddedParam = urlParams.get('embedded');

    dispatch({ type: 'setEmbedded', value: embeddedParam === 'true' });

    const activityParam = urlParams.get('campaignActivityId');

    dispatch({ type: 'setCampaignActivityId', value: activityParam });
  }, []);

  return (
    <PageStates>
      <PromoCodeControl addPromo={addPromo} removePromo={removePromo} />
      <div className="agree-checkbox-div">
        <input
          id="legalAuthorization"
          type="checkbox"
          disabled={processingPayment || !rating}
          value={agreedToTerms}
          className="Form-checkbox"
          data-qe-id="purchase-upsell-checkbox"
          onClick={() => {
            setAgreedToTerms((value) => !value);
          }}
        />
        <label
          className="agree-checkbox-label"
          htmlFor="legalAuthorization"
          data-qe-id="purchase-upsell-checkbox-label"
        >
          {t('add-inbox-preview.i-agree-to-the-terms')}
        </label>
      </div>
      <div>
        <Button
          shape="square"
          kind="primary"
          onClick={clickAccept}
          text={t(`add-inbox-preview.${processingPayment ? 'processing-payment' : 'accept'}`)}
          busy={processingPayment}
          disabled={processingPayment || !rating || !agreedToTerms}
          className="Button Button--secondary u-paddingSidesOneAndHalf u-marginRightHalf"
          data-qe-id="purchase-upsell-btn-purchase"
        />
        {!processingPayment && !embedded && (
          <a
            className="u-textUnderline trackable addon-purchase-cancel"
            data-tracked-action="cancel"
            href={homePage}
            type="button"
            data-qe-id="purchase-upsell-btn-cancel"
          >
            {t('add-inbox-preview.cancel')}
          </a>
        )}
      </div>
      <div
        className="error-msg"
        style={{
          visibility: showTermsError ? 'inherit' : 'hidden',
        }}
      >
        {t('add-inbox-preview.please-accept-the-terms')}
      </div>
      <div className="inbox-preview-upsell-legal-disclaimer">
        <Trans
          i18nKey="add-inbox-preview.purchase-details"
          values={{ count: IBP_AMOUNT, price, currency, supportPhone }}
        >
          <a href={`https://www${getEnv()}constantcontact.com/legal/terms`} target="_blank" rel="noopener noreferrer">
            Constant Contact terms and conditions
          </a>
        </Trans>
      </div>
    </PageStates>
  );
};

InboxPreviewPurchasePanel.propTypes = {
  customer: PropTypes.shape({
    getCountryCode: PropTypes.func,
  }),
};

export default InboxPreviewPurchasePanel;
